import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, RotateCcw } from "lucide-react";

const ExamResults = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { score = 0, total = 0, questionsAnswered = 0, avgTime = "—", totalTime = "—" } = location.state || {};

    const percentage = total > 0 ? Math.round((score / total) * 100) : 0;
    const r = 48;
    const circumference = 2 * Math.PI * r;
    const offset = circumference - (percentage / 100) * circumference;

    const getFeedback = () => {
        if (percentage >= 80) return {
            label: "Excellent work",
            ringColor: "#34d399",
            message: "Strong understanding across the board. You're performing at the top tier of TMUA candidates."
        };
        if (percentage >= 60) return {
            label: "Good effort",
            ringColor: "#60a5fa",
            message: "Solid foundation. A focused push on your weaker topics will get you to the next level."
        };
        if (total === 0) return {
            label: "No data",
            ringColor: "#333a4d",
            message: "No session data was found. Try starting a practice session from the Practice Hub."
        };
        return {
            label: "Keep practising",
            ringColor: "#60a5fa",
            message: "Every attempt builds familiarity. Review worked solutions and retry the topics you struggled with."
        };
    };
    const fb = getFeedback();

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>

            {/* Nav */}
            <nav className="border-b border-[var(--border-ui)] sticky top-0 bg-[var(--bg)]/90 backdrop-blur-md z-10 px-6">
                <div className="py-3.5 flex items-center justify-between">
                    <button onClick={() => navigate("/")} className="brand-btn">
                        tmua<span className="brand-prep">prep</span>
                    </button>
                    <button onClick={() => navigate("/practice")} className="text-[var(--subtle-text)] hover:text-[var(--text)] text-sm transition-colors">
                        ← Practice Hub
                    </button>
                </div>
            </nav>

            <main className="paper-grid flex-1 px-6 py-14 flex items-center">
                <div className="max-w-3xl mx-auto w-full">

                    {/* Header */}
                    <div className="mb-8">
                        <p className="eyebrow text-[#60a5fa] mb-3">
                            Session Complete
                        </p>
                        <h1 className="font-display text-[42px] tracking-tight">Your results</h1>
                    </div>

                    {/* Mark sheet — split panel */}
                    <div className="grid md:grid-cols-5 rounded-3xl border border-[var(--border-ui)] overflow-hidden bg-[var(--surface)] shadow-[var(--shadow)] mb-4">

                        {/* Left — score panel */}
                        <div className="md:col-span-2 relative bg-[var(--surface-raised)] border-b md:border-b-0 md:border-r border-[var(--border-ui)] px-8 py-10 flex flex-col items-center justify-center">
                            <div className="relative mb-6">
                                <svg width="148" height="148" viewBox="0 0 128 128" className="-rotate-90">
                                    <circle cx="64" cy="64" r={r} fill="none" stroke="var(--border-ui)" strokeWidth="6" />
                                    <circle
                                        cx="64" cy="64" r={r}
                                        fill="none"
                                        stroke={fb.ringColor}
                                        strokeWidth="6"
                                        strokeDasharray={circumference}
                                        strokeDashoffset={offset}
                                        strokeLinecap="round"
                                        style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1)", filter: `drop-shadow(0 0 10px ${fb.ringColor}44)` }}
                                    />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="num text-[30px] font-semibold text-[var(--text)] leading-none">{percentage}%</span>
                                    <span className="num text-[11px] text-[var(--subtle-text)] mt-1.5">{score} / {total}</span>
                                </div>
                            </div>
                            <div className="eyebrow" style={{ color: fb.ringColor }}>{fb.label}</div>
                        </div>

                        {/* Right — stats ledger */}
                        <div className="md:col-span-3 px-8 py-8 flex flex-col justify-center">
                            <div className="space-y-4 mb-7">
                                {[
                                    { value: `${score} / ${total}`,   label: "Score" },
                                    { value: questionsAnswered,       label: "Answered" },
                                    { value: avgTime,                 label: "Avg per question" },
                                    { value: totalTime,               label: "Total time" },
                                ].map(({ value, label }) => (
                                    <div key={label} className="flex items-baseline justify-between gap-3">
                                        <span className="eyebrow !text-[9px] text-[var(--subtle-text)] flex-shrink-0">{label}</span>
                                        <span className="flex-1 border-b border-dotted border-[var(--border-ui)] relative -top-0.5" />
                                        <span className="num text-sm text-[var(--text-soft)]">{value}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="text-[var(--muted-text)] text-[13px] leading-relaxed border-l-2 pl-4" style={{ borderColor: `${fb.ringColor}66` }}>
                                {fb.message}
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="btn-ghost"
                        >
                            <RotateCcw className="w-3 h-3" /> Try Again
                        </button>
                        <button
                            onClick={() => navigate("/practice")}
                            className="btn-primary group"
                        >
                            Practice Hub
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform duration-200" />
                        </button>
                    </div>

                </div>
            </main>

            <footer className="border-t border-[var(--border-ui)] px-6 py-6">
                <p className="text-center text-[var(--subtle-text)] text-xs">© 2025 TMUA Prep. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default ExamResults;
